import { useEffect, useState } from "react";
import IPartner from "../../../models/partner/IPartner";
import partnerService from "../../../services/PartnerService";
import { makeStyles, Title2 } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import Hero from "../../../components/hero/Hero";
import PartnerCard from "./PartnerCard";
import SEO from "../../../app/SEO";
import FeaturedPartner from "./FeaturedPartner";
import Status from "../../../enums/Status";
import { FeaturedPartnerPlaceholder, PartnerCardPlaceholder } from "./Placeholders";
import { dolphinTokens } from "../../../utils/Theme";
import { useHeroMainSnap } from "../../../hooks/useHeroMainSnap";

const useStyles = makeStyles({
    mainContainer: {
        padding: 0
    },
    featuredPartnersContainer: {
        display: "flex",
        flexWrap: "wrap",
        maxWidth: "100%",
        padding: "0 0 32px 0",
        justifyContent: "center",
        backgroundColor: dolphinTokens.colorDarkBlue,
        color: "white",
    },
    title: {
        width: "100%",
        textAlign: "center"
    },
    partnersContainer: {
        display: "flex",
        flexWrap: "wrap",
        rowGap: "16px",
        columnGap: "48px",
        padding: "32px",
        maxWidth: "100%",
        margin: "0 auto",
        justifyContent: "center",

        "@media (min-width: 768px)": {
            maxWidth: "1200px"
        }
    }
});

const Placeholder = () => {
    const { t } = useTranslation(["partners", "general"]);

    // snap behaviour for this page
    useHeroMainSnap("partners-hero", "partners-main", {
        duration: 900,
        easing: "quart",
        heroCapturePx: 80,
        upCapturePx: 300,
        headerOffset: 0
    });

    return (
        <>
            <div id="partners-hero">
                <Hero
                    title={t("hero.title")}
                    subtitle={t("hero.description")}
                    imageUri="/img/banners/partners.webp"
                    />
            </div>

            <div id="partners-main">
                <FeaturedPartnerPlaceholder />
                <PartnerCardPlaceholder />
            </div>
        </>
    );
};

const Partners = () => {
    const { t } = useTranslation(["partners", "general"]);
    const styles = useStyles();

    const [status, setStatus] = useState<Status>(Status.LOADING);
    const [partners, setPartners] = useState<IPartner[]>([]);

    useEffect(() => {
        fetchPartners(setPartners, setStatus);
    }, []);

    if (status === Status.LOADING) {
        return <Placeholder />;
    }

    const jsonLD: {}[] = [
        {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": t("companyName", { ns: "general" }),
            "url": "https://www.consultdolphin.com",
            "partner": partners.map((partner) => ({
                "@type": "Organization",
                "name": partner.name,
                "url": partner.url
            }))
        }
    ];

    return (
        <div>
            <SEO
                title={{ pageTitle: t("pageTitle"), title: t("pageTitle") }}
                canonicalPath="/partners"
                jsonLD={jsonLD}
                />

            <div className={styles.mainContainer}>
                <Hero
                    title={t("hero.title")}
                    subtitle={t("hero.description")}
                    imageUri="/img/banners/partners.webp"
                    />

                <div className={styles.featuredPartnersContainer}>
                    <Title2 block as="h2" className={styles.title}>
                        {t("featuredPartners")}
                    </Title2>
                    {
                        partners
                            .filter((p) => p.isFeatured)
                            .map((p, i) => <FeaturedPartner key={p.id} partner={p} index={i} />)
                    }
                </div>

                <Title2 block as="h2" className={styles.title}>
                    {t("otherPartners")}
                </Title2>

                <div className={styles.partnersContainer}>
                    {
                        partners
                            .filter((p) => !p.isFeatured)
                            .map((p) => <PartnerCard key={p.id} partner={p} />)
                    }
                </div>
            </div>
        </div>
    );
};

export default Partners;

async function fetchPartners(
    setPartners: (partners: IPartner[]) => void,
    setStatus: (status: Status) => void
) {
    setStatus(Status.LOADING);

    try {
        const partners = await partnerService.getPartners();
        
        setPartners(partners);
        setStatus(Status.LOAD_SUCCESS);
    } catch (error) {
        setStatus(Status.LOAD_ERROR);
    }
};